import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatMoney, todayISO } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DatePicker from "@/components/DatePicker";
import ClienteAutocomplete from "@/components/ClienteAutocomplete";
import ProductoCombobox from "@/components/ProductoCombobox";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Sparkles, Trash2, Plus, Wand2 } from "lucide-react";
import { toast } from "sonner";

const EXAMPLE = "10 redondo chico 5 milanesa pollo grande 3 miga cuadrado";

export default function NuevoPedido() {
  const navigate = useNavigate();
  const [fecha, setFecha] = useState(todayISO());
  const [cliente, setCliente] = useState("");
  const [clienteId, setClienteId] = useState(null);
  const [telefono, setTelefono] = useState("");
  const [texto, setTexto] = useState("");
  const [parsed, setParsed] = useState(null);
  const [items, setItems] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/productos").then((r) => setProductos(r.data));
  }, []);

  const procesar = async () => {
    if (!texto.trim()) {
      toast.error("Pegá el texto del pedido para procesar.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/pedidos/parse", { texto });
      setParsed(res.data);
      setItems(
        res.data.items.map((it, idx) => ({ ...it, _key: `it-${idx}-${Math.random()}` }))
      );
    } catch {
      toast.error("Error al procesar el pedido.");
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (key, patch) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it._key !== key) return it;
        const next = { ...it, ...patch };
        next.subtotal = Number(next.cantidad || 0) * Number(next.precio_unitario || 0);
        return next;
      })
    );
  };

  const setProducto = (key, p) => {
    if (!p) return;
    updateItem(key, {
      producto_id: p.id,
      producto_nombre: p.nombre,
      precio_unitario: Number(p.precio),
      needs_review: false,
      reason: null,
      candidates: [],
    });
  };

  const setProductoById = (key, productoId) => {
    const p = productos.find((x) => x.id === productoId);
    setProducto(key, p);
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((it) => it._key !== key));
  };

  const addEmptyItem = () => {
    setItems((prev) => [
      ...prev,
      {
        _key: `it-${Date.now()}`,
        producto_id: null,
        producto_nombre: "",
        cantidad: 1,
        precio_unitario: 0,
        subtotal: 0,
        needs_review: true,
        raw_text: "",
        reason: "Sin producto seleccionado",
        candidates: [],
      },
    ]);
  };

  const total = items.reduce((s, it) => s + Number(it.subtotal || 0), 0);
  const totalUnidades = items.reduce((s, it) => s + Number(it.cantidad || 0), 0);
  const hasReview = items.some((it) => it.needs_review || !it.producto_id);
  const ready = items.length > 0;

  const guardar = async (confirmar = false) => {
    if (!cliente.trim()) {
      toast.error("Ingresá el nombre del cliente.");
      return;
    }
    if (!ready) {
      toast.error("Agregá al menos un item.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        fecha,
        cliente: cliente.trim(),
        cliente_id: clienteId || null,
        telefono,
        texto_original: texto,
        items: items.map(({ _key, candidates, ...rest }) => ({
          ...rest,
          cantidad: Number(rest.cantidad),
          precio_unitario: Number(rest.precio_unitario),
          subtotal: Number(rest.subtotal),
        })),
      };
      const res = await api.post("/pedidos", payload);
      const pedido = res.data;
      if (confirmar && !hasReview) {
        try {
          await api.post(`/pedidos/${pedido.id}/confirmar`);
          toast.success(`Pedido ${pedido.pedido_id} confirmado y remito generado.`);
        } catch {
          toast.success(`Pedido ${pedido.pedido_id} creado.`);
        }
      } else {
        toast.success(`Pedido ${pedido.pedido_id} creado en estado ${pedido.estado}.`);
      }
      navigate(`/admin/pedidos/${pedido.id}`);
    } catch {
      toast.error("Error al guardar el pedido.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-28 lg:pb-0">
      <PageHeader
        eyebrow="Carga de pedido"
        title="Nuevo Pedido"
        subtitle='Pegá el texto del cliente — el sistema interpreta cantidades y productos. Ej: "10 redondo chico 5 milanesa pollo grande".'
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: texto */}
        <Card className="lg:col-span-2 p-6 border-[#E5E0D8] rounded-2xl bg-white">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <Label className="text-[12px] font-semibold text-[#595959] mb-1.5 block">Fecha</Label>
              <DatePicker
                value={fecha}
                onChange={setFecha}
                testid="np-fecha"
                allowClear={false}
              />
            </div>
            <div>
              <Label className="text-[12px] font-semibold text-[#595959] mb-1.5 block">Teléfono (opcional)</Label>
              <Input
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="+54 11..."
                data-testid="np-telefono"
                className="bg-white h-10"
              />
            </div>
          </div>
          <div className="mb-4">
            <Label className="text-[12px] font-semibold text-[#595959] mb-1.5 block">Cliente</Label>
            <ClienteAutocomplete
              value={cliente}
              cliente_id={clienteId}
              onChange={({ nombre, cliente_id, telefono: tel }) => {
                setCliente(nombre);
                setClienteId(cliente_id);
                if (tel && !telefono) setTelefono(tel);
              }}
              testid="np-cliente"
            />
          </div>
          <div className="mb-3 flex items-center justify-between">
            <Label className="text-[12px] font-semibold text-[#595959]">Texto del pedido</Label>
            <button
              type="button"
              onClick={() => setTexto(EXAMPLE)}
              className="text-[11px] text-[#C85A32] hover:underline"
              data-testid="np-ejemplo"
            >
              Ver ejemplo
            </button>
          </div>
          <Textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder='Ej: "10 redondo chico 5 milanesa pollo grande 3 miga cuadrado"'
            className="font-mono text-sm min-h-[180px] resize-y bg-white"
            data-testid="np-texto"
          />
          <Button
            onClick={procesar}
            disabled={loading}
            className="mt-4 w-full bg-[#C85A32] hover:bg-[#A64A29] text-white h-11 rounded-lg gap-2"
            data-testid="np-procesar"
          >
            <Wand2 className="h-4 w-4" />
            {loading ? "Procesando..." : "Procesar pedido"}
          </Button>

          {parsed?.error && (
            <div className="mt-4 p-3 rounded-lg bg-[#FBEBEB] text-[#8E2E2E] text-sm border border-[#F0CECE]">
              {parsed.error}
            </div>
          )}
        </Card>

        {/* Right: parsed result */}
        <Card className="lg:col-span-3 p-6 border-[#E5E0D8] rounded-2xl bg-white" data-testid="np-resultado">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C]">
                Resultado del parser
              </div>
              <h2 className="font-display font-bold text-2xl text-[#1A1A1A]">Items detectados</h2>
            </div>
            {hasReview && items.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full bg-[#FDF3E1] text-[#A6751F] border border-[#F0DEB2]" data-testid="np-revisar-flag">
                <AlertTriangle className="h-3.5 w-3.5" /> Revisar
              </span>
            )}
          </div>

          {items.length === 0 && (
            <div className="border border-dashed border-[#E5E0D8] rounded-xl p-10 text-center" data-testid="np-empty">
              <Sparkles className="h-8 w-8 mx-auto text-[#C85A32] mb-3" />
              <div className="font-display font-semibold text-lg text-[#1A1A1A]">
                Aún no hay items procesados
              </div>
              <p className="text-sm text-[#595959] mt-1">
                Pegá un texto y presioná <span className="font-semibold">Procesar pedido</span>.
              </p>
            </div>
          )}

          {items.length > 0 && (
            <div className="space-y-3">
              {items.map((it, idx) => (
                <ItemRow
                  key={it._key}
                  item={it}
                  index={idx}
                  productos={productos}
                  onPickCandidate={(p) => setProducto(it._key, p)}
                  onChangeProducto={(pid) => setProductoById(it._key, pid)}
                  onChangeQty={(q) => updateItem(it._key, { cantidad: Math.max(1, Number(q || 1)) })}
                  onChangePrecio={(p) => updateItem(it._key, { precio_unitario: Math.max(0, Number(p || 0)) })}
                  onRemove={() => removeItem(it._key)}
                />
              ))}

              <button
                type="button"
                onClick={addEmptyItem}
                className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-[#E5E0D8] text-sm text-[#595959] hover:bg-[#F9F6F0] transition-colors"
                data-testid="np-add-item"
              >
                <Plus className="h-4 w-4" /> Agregar item manual
              </button>

              {/* Desktop totals + actions */}
              <div className="hidden lg:flex mt-6 pt-5 border-t border-[#E5E0D8] flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C]">
                    Total · {totalUnidades} u
                  </div>
                  <div className="font-display font-bold text-3xl text-[#C85A32]" data-testid="np-total">
                    {formatMoney(total)}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    className="border-[#E5E0D8] hover:bg-[#F9F6F0] h-11"
                    onClick={() => guardar(false)}
                    disabled={saving}
                    data-testid="np-guardar-borrador"
                  >
                    Guardar
                  </Button>
                  <Button
                    className="bg-[#6B705C] hover:bg-[#545848] text-white h-11"
                    onClick={() => guardar(true)}
                    disabled={saving || hasReview}
                    data-testid="np-confirmar"
                  >
                    Confirmar y generar remito
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Mobile sticky action bar */}
      {items.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-[#E5E0D8] px-4 py-3 flex items-center justify-between gap-3 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">{totalUnidades} u · Total</div>
            <div className="font-display font-bold text-xl text-[#C85A32] leading-tight">{formatMoney(total)}</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-[#E5E0D8] h-10" onClick={() => guardar(false)} disabled={saving} data-testid="np-guardar-mobile">
              Guardar
            </Button>
            <Button className="bg-[#6B705C] hover:bg-[#545848] text-white h-10" onClick={() => guardar(true)} disabled={saving || hasReview} data-testid="np-confirmar-mobile">
              Confirmar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemRow({ item, index, productos, onPickCandidate, onChangeProducto, onChangeQty, onChangePrecio, onRemove }) {
  const review = item.needs_review || !item.producto_id;
  const cands = item.candidates || [];
  return (
    <div
      className={`fade-row rounded-xl border p-3 sm:p-4 ${
        review ? "border-[#F0DEB2] bg-[#FDF8EE]" : "border-[#E5E0D8] bg-white"
      }`}
      style={{ animationDelay: `${index * 30}ms` }}
      data-testid={`np-item-${index}`}
    >
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-3 sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Cant.</Label>
          <Input
            type="number"
            min={1}
            value={item.cantidad}
            onChange={(e) => onChangeQty(e.target.value)}
            className="h-10 text-center font-semibold"
            data-testid={`np-item-${index}-cantidad`}
          />
        </div>
        <div className="col-span-9 sm:col-span-6">
          <Label className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Producto</Label>
          <ProductoCombobox
            value={item.producto_id || null}
            productos={productos}
            onChange={(p) => onChangeProducto(p.id)}
            review={review}
            currentName={item.producto_nombre}
            testid={`np-item-${index}-producto`}
          />
        </div>
        <div className="col-span-7 sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Precio</Label>
          <Input
            type="number"
            value={item.precio_unitario}
            onChange={(e) => onChangePrecio(e.target.value)}
            className="h-10 font-mono"
            data-testid={`np-item-${index}-precio`}
          />
        </div>
        <div className="col-span-3 sm:col-span-1 text-right">
          <Label className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Subtotal</Label>
          <div className="h-10 flex items-center justify-end font-mono font-semibold text-[#1A1A1A]" data-testid={`np-item-${index}-subtotal`}>
            {formatMoney(item.subtotal)}
          </div>
        </div>
        <div className="col-span-2 sm:col-span-1 flex justify-end">
          <button
            type="button"
            onClick={onRemove}
            className="h-10 w-10 rounded-md hover:bg-[#FBEBEB] text-[#8E2E2E] flex items-center justify-center transition-colors"
            aria-label="Quitar item"
            data-testid={`np-item-${index}-remove`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {review && (
        <div className="mt-2.5">
          <div className="flex items-center gap-2 text-[12px] text-[#A6751F]">
            <AlertTriangle className="h-3.5 w-3.5" />
            {item.reason || "Necesita revisión"}
            {item.raw_text && (
              <span className="font-mono text-[#8C8C8C]">— texto: "{item.raw_text}"</span>
            )}
          </div>
          {cands.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`np-item-${index}-candidates`}>
              <span className="text-[10px] uppercase tracking-wider text-[#8C8C8C] self-center mr-1">¿Cuál?</span>
              {cands.map((c) => (
                <button
                  key={c.producto_id}
                  type="button"
                  onClick={() =>
                    onPickCandidate(
                      productos.find((p) => p.id === c.producto_id) || {
                        id: c.producto_id,
                        nombre: c.producto_nombre,
                        precio: c.precio,
                      }
                    )
                  }
                  className="px-2.5 py-1 rounded-full bg-white border border-[#E5E0D8] text-[12px] text-[#1A1A1A] hover:bg-[#F4EAE6] hover:border-[#C85A32] transition-colors"
                  data-testid={`np-item-${index}-cand-${c.producto_id}`}
                >
                  <span className="font-medium">{c.producto_nombre}</span>{" "}
                  <span className="text-[#8C8C8C] font-mono text-[11px]">{formatMoney(c.precio)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
